import React from "react";
import { toast } from "react-toastify";
import { getAllBranches } from "../../services/BranchesService";
import { getClientsSelect } from "../../services/ClientsService";
import { getAllCoaches } from "../../services/CoachesService";
import { getAllClassCategories } from "../../services/ClassesService";
import { getErrorMessage, unwrapList } from "../../utils/apiResponse";
import { isCoachActive } from "./classUtils";
import { UnitOption } from "./types";

export type Option = { label: string; value: string };

/** Units (courts/rooms) of the current business. */
export const useUnitOptions = (businessId?: string, enabled: boolean = true) => {
  const [units, setUnits] = React.useState<UnitOption[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled || !businessId) return;
    let cancelled = false;

    const fetchUnits = async () => {
      setLoading(true);
      try {
        const response = await getAllBranches(businessId);
        const mapped = unwrapList(response, "units").map((u: any) => ({
          id: String(u.id ?? u.unit_id ?? u._id),
          name: u.name || u.unit_name || "Unknown Unit",
        }));
        if (!cancelled) setUnits(mapped);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to load units."));
          setUnits([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUnits();
    return () => {
      cancelled = true;
    };
  }, [businessId, enabled]);

  const options: Option[] = React.useMemo(
    () => units.map((u) => ({ label: u.name, value: u.id })),
    [units]
  );

  return { units, options, loading };
};

/** Clients of the business, for booking / purchasing on their behalf. */
const toClientOption = (c: any): Option => {
  const phone = c.mobile || c.phone || "";
  const name = c.name || c.full_name || "";
  // `label` from the API is already "name - phone" pre-formatted — only
  // fall back to it when there's no plain name, and use it as-is so the
  // phone isn't appended a second time.
  const display = name
    ? phone
      ? `${name} — ${phone}`
      : name
    : c.label || "Unnamed client";
  return {
    label: display,
    value: String(c.id),
  };
};

const CLIENT_OPTIONS_INITIAL_LIMIT = 50;

/**
 * The first clients of the business up front, then server matches merged in
 * as the user types: pass `onSearchInput` to the picker's CustomSelect.
 */
export const useClientOptions = (
  businessId?: string,
  enabled: boolean = true,
  /** Clients that must be listed even when outside the first page. */
  includeId?: string
) => {
  const [options, setOptions] = React.useState<Option[]>([]);
  const [loading, setLoading] = React.useState(false);
  const searchTimerRef = React.useRef<number | undefined>(undefined);

  const mergeOptions = React.useCallback((incoming: Option[]) => {
    setOptions((prev) => {
      const byValue = new Map(prev.map((option) => [option.value, option]));
      incoming.forEach((option) => byValue.set(option.value, option));
      return Array.from(byValue.values());
    });
  }, []);

  React.useEffect(() => {
    if (!enabled || !businessId) return;
    let cancelled = false;

    const fetchClients = async () => {
      setLoading(true);
      try {
        const response = await getClientsSelect({
          businessId,
          ids: includeId ? [includeId] : undefined,
          limit: CLIENT_OPTIONS_INITIAL_LIMIT,
        });
        const mapped = unwrapList(response, "clients").map(toClientOption);
        if (!cancelled) setOptions(mapped);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to load clients."));
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchClients();
    return () => {
      cancelled = true;
    };
  }, [businessId, enabled, includeId]);

  const onSearchInput = React.useCallback(
    (term: string) => {
      window.clearTimeout(searchTimerRef.current);
      const trimmed = term.trim();
      if (!businessId || trimmed.length < 2) return;
      searchTimerRef.current = window.setTimeout(async () => {
        try {
          const response = await getClientsSelect({ businessId, search: trimmed });
          mergeOptions(unwrapList(response, "clients").map(toClientOption));
        } catch {
          // The picker keeps the clients it already has.
        }
      }, 300);
    },
    [businessId, mergeOptions]
  );

  React.useEffect(() => () => window.clearTimeout(searchTimerRef.current), []);

  return { options, loading, onSearchInput };
};

/** Coaches in the organization (active ones first). */
export const useCoachOptions = (enabled: boolean = true) => {
  const [options, setOptions] = React.useState<Option[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const fetchCoaches = async () => {
      setLoading(true);
      try {
        const coaches = await getAllCoaches();
        const mapped = coaches
          .filter((c: any) => isCoachActive(c))
          .map((c: any) => ({
            label: c.full_name || c.name || "Unnamed coach",
            value: String(c.id),
          }));
        if (!cancelled) setOptions(mapped);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to load coaches."));
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCoaches();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { options, loading };
};

/** Class categories, refetchable so a newly created one shows up immediately. */
export const useCategoryOptions = (
  enabled: boolean = true,
  refetchToken: number = 0
) => {
  const [options, setOptions] = React.useState<Option[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const fetchCategories = async () => {
      setLoading(true);
      try {
        const categories = await getAllClassCategories();
        const mapped = categories
          .filter((c: any) => c?.active !== false)
          .map((c: any) => ({
            label: c.name || "Unnamed category",
            value: String(c.id),
          }));
        if (!cancelled) setOptions(mapped);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to load class categories."));
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCategories();
    return () => {
      cancelled = true;
    };
  }, [enabled, refetchToken]);

  return { options, loading };
};
